import { CardString } from 'pokersolver'

import { ActionHint } from '../ActionEvaluator'
import { Player } from '../Player'
import { TableSnapshot } from '../Snapshot'
import {
  hasAceSuited,
  hasAPair,
  hasFaceCards,
  hasFlushDraw,
  hasHighCard,
  hasStraightDraw,
  hasSuitedConnectors,
  BotAction
} from './BotPlayerKind.utils'

import { BoardStates, BoardTag } from '../states'
import { Game } from '../Game'

type BoardStateTag = Exclude<Omit<BoardTag, 'description'>['tag'], 'predeal' | 'showdown'>

type BotStrategyConfig = {
  hasRandomFactor: boolean
}

export class BotPlayerKind {
  /**
   *
   * @param mainPot main pot amount
   * @param playerChips player chips
   * @returns the amount to bet
   */
  betAmount({
    mainPot,
    playerChips,
    bigBlindAmount
  }: {
    mainPot: number
    playerChips: number
    bigBlindAmount: number
  }): number {
    const halfBetFromMainPot = Math.floor(mainPot / 2)

    if (halfBetFromMainPot < bigBlindAmount) {
      // minimum bet should always be the BB amount
      // what if playerChips are less than BB amount?
      return Math.min(bigBlindAmount, playerChips)
    }

    const betAmount = Math.floor(Math.min(halfBetFromMainPot, playerChips))
    return betAmount
  }

  /**
   *
   * @param biggestBet last biggest bet
   * @param playerChips player chips
   * @param boardState board state
   * @see {@link BoardStates} for the possible board state values
   * @returns the amount to raise
   */
  raiseAmount({ biggestBet, playerChips }: { biggestBet: number; playerChips: number }): number {
    const raiseAmount = Math.floor(Math.min(3 * biggestBet, playerChips))
    return raiseAmount
  }

  hasGoodHand(cards: CardString[], boardState: BoardStateTag): boolean {
    const boardStateExecutor = {
      [BoardStates.Deal.tag]: () =>
        hasAPair(cards) || hasFaceCards(cards) || hasAceSuited(cards) || hasSuitedConnectors(cards),
      [BoardStates.Flop.tag]: () => !hasHighCard(cards) || hasFlushDraw(cards) || hasStraightDraw(cards),
      [BoardStates.Turn.tag]: () => !hasHighCard(cards) || hasFlushDraw(cards) || hasStraightDraw(cards),
      [BoardStates.River.tag]: () => !hasHighCard(cards)
    }

    const isGoodHand = boardStateExecutor[boardState]()

    return isGoodHand
  }

  getAction({ player, table }: { game: Game; player: Player; table: TableSnapshot }) {
    return this.getRandomAction(player, table)
  }

  getRandomAction(player: Player, table: TableSnapshot) {
    const posibleActions = [...player.actions]
    if (!(posibleActions?.length && table.boardState)) return

    const hasGoodHand = this.hasGoodHand([...player.cards, ...table.communityCards], table.boardState as BoardStateTag)
    const shouldPlayHand = hasGoodHand

    if (posibleActions.includes(ActionHint.FOLD)) {
      if (!shouldPlayHand) return { action: ActionHint.FOLD, amount: 0 }
      posibleActions.splice(posibleActions.indexOf(ActionHint.FOLD), 1)
    }

    let action = posibleActions[Math.floor(Math.random() * posibleActions.length)]

    if (posibleActions.includes(ActionHint.BET)) {
      if (table.biggestBet === 0) {
        // there is no bet yet
        action = ActionHint.BET
      }
    } else if (table.biggestBet === table.bigBlindAmount) {
      if (posibleActions.includes(ActionHint.RAISE)) {
        action = ActionHint.RAISE
      }
    }

    const amount = this.mapAmount(action, player.chips, table.mainPot, table.biggestBet, table.bigBlindAmount)

    if ([ActionHint.RAISE, ActionHint.BET].includes(action) && amount <= table.minimumRaise) {
      action = ActionHint.ALLIN
    }

    return { action, amount }
  }

  mapAmount(action: ActionHint, playerChips: number, mainPot: number, biggestBet: number, bigBlindAmount: number) {
    const map: Record<string, () => number> = {
      [ActionHint.ALLIN]: () => 0, // noop. Handled by table doAllInForany
      [ActionHint.BET]: () => this.betAmount({ mainPot, playerChips, bigBlindAmount }),
      [ActionHint.BLANK]: () => 0,
      [ActionHint.CALL]: () => 0,
      [ActionHint.CHECK]: () => 0,
      [ActionHint.FOLD]: () => 0,
      [ActionHint.RAISE]: () => this.raiseAmount({ biggestBet, playerChips })
    }
    return map[action]()
  }
}
